import type { ReactNode } from "react";
import { RgbaColorPicker } from "react-colorful";
import type { ColorValue } from "~/lib/types";
import type { ControlSpec } from "~/nodes/types";

interface NodeControlProps {
  spec: ControlSpec;
  value: unknown;
  /** Present when `compute` flagged this control as invalid. */
  error?: string | undefined;
  /** Computed read-only value, for `preview` controls. */
  preview?: number | undefined;
  onChange: (key: string, value: unknown) => void;
}

const FIELD =
  "w-full rounded-md border bg-neutral-900/70 px-2 py-1 text-sm text-neutral-100 focus:outline-none";

/**
 * Renders one control from a node definition. `nodrag`/`nowheel` stop xyflow
 * from panning the canvas or zooming while the field is being used — the rete
 * version needed a manual `pointerdown` listener on every input for this.
 */
export function NodeControl({
  spec,
  value,
  error,
  preview,
  onChange,
}: NodeControlProps) {
  const border = error
    ? "border-rose-500"
    : "border-neutral-600 focus:border-teal-400";

  switch (spec.kind) {
    case "number":
      return (
        <Labelled label={spec.label ?? spec.key} error={error}>
          <input
            type="number"
            step={spec.step ?? 0.1}
            value={numeric(value)}
            onChange={(event) => onChange(spec.key, event.target.value)}
            className={`nodrag ${FIELD} ${border}`}
          />
        </Labelled>
      );

    case "text":
      return (
        <Labelled label={spec.label ?? spec.key} error={error}>
          <input
            type="text"
            spellCheck={false}
            value={String(value ?? "")}
            onChange={(event) => onChange(spec.key, event.target.value)}
            className={`nodrag font-mono ${FIELD} ${border}`}
          />
        </Labelled>
      );

    case "code":
      return (
        <Labelled label={spec.label ?? spec.key} error={error}>
          <textarea
            rows={spec.rows ?? 10}
            spellCheck={false}
            value={String(value ?? "")}
            onChange={(event) => onChange(spec.key, event.target.value)}
            className={`nodrag nowheel scrollbar-slim resize-y font-mono text-xs leading-snug ${FIELD} ${border}`}
          />
        </Labelled>
      );

    case "toggle":
      return (
        <label className="nodrag flex cursor-pointer items-center gap-2 py-1 text-xs text-neutral-300">
          <input
            type="checkbox"
            checked={value === true}
            onChange={(event) => onChange(spec.key, event.target.checked)}
            className="size-4 accent-teal-500"
          />
          {spec.label ?? spec.key}
        </label>
      );

    case "color": {
      const color = (value as ColorValue | undefined)?.rgb ?? {
        r: 100,
        g: 100,
        b: 200,
        a: 1,
      };

      return (
        <div className="nodrag nowheel flex justify-center py-1">
          <RgbaColorPicker
            color={color}
            onChange={(rgb) => onChange(spec.key, { rgb })}
          />
        </div>
      );
    }

    case "preview":
      return (
        <Labelled label={spec.label ?? spec.key} error={error}>
          <output
            className={`block truncate rounded-md border border-neutral-700 bg-neutral-950/60 px-2 py-1 font-mono text-sm ${
              error ? "text-rose-400" : "text-teal-300"
            }`}
          >
            {preview === undefined ? "—" : preview.toFixed(3)}
          </output>
        </Labelled>
      );
  }
}

function Labelled({
  label,
  error,
  children,
}: {
  label: string;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <label className="block py-0.5">
      <span className="block text-[10px] font-medium tracking-wide text-neutral-400 uppercase">
        {label}
      </span>
      {children}
      {error ? (
        <span className="mt-0.5 block text-[10px] text-rose-400">{error}</span>
      ) : null}
    </label>
  );
}

/** Keeps the input controlled while allowing intermediate text like "-" or "1.". */
function numeric(value: unknown): string | number {
  if (typeof value === "number") return value;
  return typeof value === "string" ? value : "";
}
